import { Fragment, ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { colors } from '@karnott/colors';
import { fontFamily, msDuration, pixelSize, pixelSpacing } from '@karnott/theme';
import { color, spacing, zIndex } from './theme';

export const List = styled.ul`
  list-style-type: none;
  padding-left: 0;
  margin-top: 0;
  margin-bottom: 0;
`;

export const InlineList = styled(List)`
  > li {
    display: inline-block;
  }
`;

export const Link = styled(NavLink)`
  color: inherit;
  text-decoration: none;
  display: inline-block;
`;

export const EntityLink = styled(NavLink)`
  color: inherit;
  font-weight: 700;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const EntityLinkSub = styled.div`
  color: inherit;
  font-weight: 300;
  text-decoration: none;
`;

export const ClickableEntity = styled.div`
  color: inherit;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  &:hover {
    text-decoration: underline;
  }
`;

export const Container = styled.div<{ bare?: boolean; withMargin?: boolean }>`
  padding-left: ${(props) => (props.bare ? 0 : spacing)}px;
  padding-right: ${(props) => (props.bare ? 0 : spacing)}px;
  margin-left: ${(props) => (props.withMargin ? pixelSpacing('large') : 0)};
  margin-right: auto;
  max-width: 100%;
  width: ${(props) => (props.withMargin ? 'unset' : '100%')};
`;

export const Table = styled.table`
  width: 100%;
`;

export const Tr = styled.tr`
  vertical-align: middle;
  color: ${color('smoke', 'dark')};
`;

export const Td = styled.td`
  padding: ${spacing / 2}px;
`;

export const Grid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${pixelSpacing('medium')};
`;

export const VerticalSplitContainer = styled.div<{ isDemo?: boolean }>`
  display: flex;
  flex: 1;
  background-color: ${colors('grey', 100)};
  overflow-x: hidden;

  @media (max-width: 1180px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

export const RightContainer = styled.div`
  display: flex;
  flex: 2;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  min-width: 0;
`;

export const RightContainerScrollableContent = styled.div<{ isDemo?: boolean; $fill?: boolean }>`
  display: flex;
  flex-direction: column;
  ${({ $fill }) => ($fill ? 'flex-grow: 1;' : '')}
  align-items: stretch;
  overflow-x: hidden;
  @media (min-width: 1180px) {
    overflow-y: auto;
  }
`;

export const Cell = styled.div`
  flex: 1;
  min-width: 0;
  @media (orientation: landscape) and (max-width: 1180px), (max-width: 1180px) {
    flex: 0 0 100%;
  }
`;

export const DetailsMapContainer = styled.div<{ fullScreenMap?: boolean; isDemo?: boolean }>`
  display: flex;
  flex: 1;
  min-height: 0;
  z-index: ${({ fullScreenMap }) => (fullScreenMap ? zIndex('ui_date_picker', 2) : zIndex('modal_map'))};
  position: ${({ fullScreenMap }) => (fullScreenMap ? 'static' : 'relative')};
  width: ${({ fullScreenMap }) => (fullScreenMap ? '100vw' : 'auto')};

  ${({ fullScreenMap }) =>
    fullScreenMap
      ? null
      : css`
          @media (max-width: 1180px) {
            flex: none;
            height: 50vh;
            max-height: 400px;
          }
        `}
`;

export const EntityDetailsContainer = styled(Container)`
  display: flex;
  flex-direction: column;
  @media (orientation: landscape) and (max-width: 700px), (max-width: 700px) {
    height: auto;
  }
`;

export const GoOnLargerScreen = styled.div`
  display: none;
  visibility: hidden;
  align-items: center;
  position: relative;
  justify-content: center;
  margin-left: 0 !important;
  padding: ${spacing / 2}px 0;

  @media (orientation: landscape) and (max-width: 700px), (max-width: 700px) {
    display: flex;
    visibility: visible;
  }
`;

export const ArrowPopover = styled.div<{
  left?: boolean;
  right?: boolean;
  popoverTop?: number;
  popoverBottom?: number;
  popoverLeft?: number;
  popoverRight?: number;
  popoverWidth?: number;
  padding?: number;
  noWhiteSpace?: boolean;
}>`
  position: absolute;
  bottom: ${(props) => (props.popoverBottom !== undefined ? `${props.popoverBottom}px` : 'auto')};
  top: ${(props) => (props.popoverTop !== undefined ? `${props.popoverTop}px` : 'auto')};
  background: ${color('snow')};
  border: 2px solid ${color('smoke', 'dark')};
  color: ${colors('grey', 'light')};
  border-radius: 4px;
  padding: ${(props) => (props.padding ? `${props.padding}px` : '4px')};
  display: inline-block;
  width: ${(props) => (props.popoverWidth ? `${props.popoverWidth}px` : 'auto')};
  left: ${(props) => (props.popoverLeft !== undefined ? `${props.popoverLeft}px` : 'auto')};
  right: ${(props) => (props.popoverRight ? `${props.popoverRight}px` : 'auto')};
  box-shadow: rgba(0, 0, 0, 0.1) 0 6px 6px;
  white-space: ${(props) => {
    if (props.noWhiteSpace) {
      return 'normal';
    }
    if (props.left || props.right) {
      return 'nowrap';
    }
    return 'normal';
  }};
  z-index: ${zIndex('modal', 3)};
  &:after,
  &:before {
    top: ${(props) => {
      if (props.left || props.right) {
        return '50%';
      }
      return '100%';
    }};
    left: ${(props) => {
      if (props.left) {
        return 'auto';
      }
      if (props.right) {
        return '100%';
      }
      return '50%';
    }};
    right: ${(props) => {
      if (props.left) {
        return '100%';
      }
      return 'auto';
    }};
    border: solid transparent;
    content: ' ';
    height: 0;
    width: 0;
    position: absolute;
    pointer-events: none;
  }
  &:after {
    border-color: rgba(242, 240, 237, 0);
    border-top-color: ${(props) => (props.left || props.right ? 'none' : color('snow'))};
    border-right-color: ${(props) => (props.left ? color('snow') : 'none')};
    border-left-color: ${(props) => (props.right ? color('snow') : 'none')};
    border-width: 6px;
    margin-left: ${(props) => (props.left || props.right ? '0' : '-6px')};
    margin-top: ${(props) => (props.left || props.right ? '-6px' : '0')};
  }
  &:before {
    border-color: rgba(203, 198, 191, 0);
    border-top-color: ${(props) => (props.left || props.right ? 'none' : color('smoke', 'dark'))};
    border-right-color: ${(props) => (props.left ? color('smoke', 'dark') : 'none')};
    border-left-color: ${(props) => (props.right ? color('smoke', 'dark') : 'none')};
    border-width: 9px;
    margin-left: ${(props) => (props.left || props.right ? '0' : '-9px')};
    margin-top: ${(props) => (props.left || props.right ? '-9px' : '0')};
  }
`;

export const CustomLink = styled.a<{ textColor?: string }>`
  font-family: ${fontFamily()};
  text-decoration: none;
  color: ${(props) => (props.textColor ? props.textColor : 'white')};
  &:visited {
    text-decoration: none;
    color: ${(props) => (props.textColor ? props.textColor : 'white')};
  }
  &:hover {
    cursor: pointer;
  }
`;

export const CustomImageLink = styled.a`
  background: #c2324f;
  border-radius: 50%;
  img {
    width: 23px;
    height: 23px;
  }
`;

const BreadcrumbWrapper = styled.div<{ $noMargin?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${pixelSpacing('small')};
  width: 100%;
  padding: ${(props) => (props.$noMargin ? 0 : `${spacing}px 0 ${spacing / 2}px 0`)};
  color: ${colors('grey', 600)};
  font-size: ${pixelSize()};
`;

const BreadcrumbLink = styled(NavLink)`
  font-weight: bold;
  text-decoration: none;

  &,
  &:visited,
  &:active {
    color: ${colors('bleen')};
  }

  &:hover {
    text-decoration: underline;
  }
`;

const BreadcrumbSeparator = styled.span`
  color: ${colors('grey', 500)};
  user-select: none;
`;

export const Overlay = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(52, 49, 44, 0.64); //#34312C
  z-index: ${zIndex('modal_modal', -2)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

export const Buttons = styled.div<{ left?: boolean }>`
  display: flex;
  position: relative;
  text-align: ${(props) => (props.left ? 'left' : 'center')};
  margin-top: ${spacing / 2}px;
  margin-bottom: 0;
  flex-shrink: 0;
  gap: ${pixelSpacing('small')} ${pixelSpacing('medium')};
`;

export const ActionButtons = styled(Buttons)<{ show?: boolean }>`
  margin-top: 0;
  justify-content: flex-end;
  opacity: ${(props) => (props.show ? 1 : 0)};
  transition: opacity 0.2s;
`;

export const OverlayCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pixelSpacing('medium')};
  align-items: center;
  background-color: ${colors('white')};
  width: 100%;
  max-width: 325px;
  border: 1px solid ${colors('orange', 400)};
  border-radius: 10px;
  padding: ${pixelSpacing()};
`;

export const OverlayText = styled.p`
  opacity: 1;
  text-align: center;
  color: ${colors('white')};
  font-size: 1.5em;
  max-width: 100%;
  padding: 0 48px;
`;

export const EntityDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  padding: ${pixelSpacing()};
  padding-top: ${pixelSpacing('small')};
`;

export const EntitySessionsWrapper = styled(EntityDataWrapper)`
  position: relative;
  padding: ${pixelSpacing()};
  > * {
    margin: 0 0 ${pixelSpacing('small')} 0;
  }
`;

// the loader fills its closest positioned ancestor, so it needs a box of its own to sit in
export const EntitySessionsLoader = styled.div`
  position: relative;
  min-height: 200px;
`;

export const FAQLink = styled.a<{ inline?: boolean }>`
  font-size: ${pixelSize()};
  color: ${colors('orange')};
  cursor: pointer;
  font-family: ${fontFamily()};
  width: fit-content;
  transition: color ${msDuration('short')} ease-in-out;
  ${({ inline }) =>
    inline
      ? null
      : css`
          margin-left: ${pixelSpacing('large')};
          margin-bottom: ${pixelSpacing('small')};
        `}
  &:hover {
    color: ${colors('orange', 600)};
  }
`;

export const SessionsButtonWrapper = styled.div`
  margin: ${pixelSpacing()} 0;
  display: flex;
  justify-content: flex-end;
`;

type BreadcrumbItem = {
  label: string;
  to: string;
};
type BreadcrumbProps = {
  current: ReactNode;
  items: BreadcrumbItem[];
  noMargin?: boolean;
};

export const Breadcrumb = function ({ current, items, noMargin = false }: BreadcrumbProps) {
  return (
    <BreadcrumbWrapper $noMargin={noMargin} data-testid="breadcrumb">
      {items.map((item) => (
        <Fragment key={item.label}>
          <BreadcrumbLink to={item.to}>{item.label}</BreadcrumbLink>
          <BreadcrumbSeparator>›</BreadcrumbSeparator>
        </Fragment>
      ))}
      <div>{current}</div>
    </BreadcrumbWrapper>
  );
};
